import { useParams } from 'react-router-dom';

import { Dispatch, useAppDispatch, useAppSelector } from 'src/store';
import classTopicActions, {
  selectContentByIdWithClassTopic,
  selector,
} from 'src/store/model/class_topic';
import { useCallback, useState } from 'react';
import {
  createUserAssignmentFile,
  UserAssignmentFilePayload,
  updateUserAssignmentFile,
} from 'src/thunks/user_assignment';
import {
  markCompleted,
  updateProgress,
} from 'src/thunks/class_topic_content_competition';

export type Content = Partial<Model.ClassTopicContents> & {
  class_topic: Model.ClassTopic | undefined;
};

export type Returned = {
  content: Content;
  createSubmission: (payload: UserAssignmentFilePayload) => void;
  updateSubmission: (payload: UserAssignmentFilePayload) => void;
  markContentCompleted: (
    class_user?: Model.ClassUser,
    is_completed?: boolean,
  ) => Promise<any>;
  loading: boolean;
  user: Model.Me;
  class_id?: string;
  class_topic_content_id?: string;
  dispatch: Dispatch;
};

const useClassTopicContent = (): Returned => {
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const { class_topic_content_id, class_topic_id, class_id } = useParams<{
    class_topic_content_id: string;
    class_topic_id: string;
    class_id: string;
  }>();
  const content = useAppSelector((state) =>
    selectContentByIdWithClassTopic(state, class_topic_content_id!),
  );
  const classTopic = useAppSelector((state) =>
    selector.selectById(state, class_topic_id!),
  );
  const user = useAppSelector(useCallback((state) => state.ui.app.me!, []));

  const createSubmission = useCallback(
    async (payload: UserAssignmentFilePayload) => {
      setLoading(true);
      const res = await dispatch(
        createUserAssignmentFile({
          ...payload,
          user_id: user?.id,
        }),
      );

      if (createUserAssignmentFile.fulfilled.match(res)) {
        dispatch(
          classTopicActions.updateHomeworkStatus({
            class_topic: classTopic!,
            checked: true,
          }),
        );
      }

      setLoading(false);
    },
    [user?.id],
  );

  const updateSubmission = useCallback(
    async (payload: UserAssignmentFilePayload) => {
      setLoading(true);
      await dispatch(
        updateUserAssignmentFile({ ...payload, user_id: user?.id }),
      );
      setLoading(false);
    },
    [user?.id],
  );

  const markContentCompleted = useCallback(
    async (classUser?: Model.ClassUser, is_completed?: boolean) => {
      setLoading(true);

      const result = await dispatch(
        markCompleted({
          class_id,
          class_topic_content_id,
          class_user_id: classUser?.id,
          is_completed,
        }),
      );

      if (markCompleted.fulfilled.match(result)) {
        await dispatch(
          updateProgress({
            class_user: classUser!,
            class_id,
            pathName: location.pathname,
            isCompleted: is_completed,
          }),
        );
      }

      setLoading(false);
    },
    [
      class_topic_content_id,
      content.class_topic_content_completion?.is_completed,
    ],
  );

  return {
    content: {
      ...content,
      class_topic: classTopic || content.class_topic,
      class_topic_id: classTopic?.id || content.class_topic_id,
    },
    createSubmission,
    updateSubmission,
    loading,
    user,
    class_id,
    class_topic_content_id,
    markContentCompleted,
    dispatch,
  };
};

export default useClassTopicContent;
